/**
 * 备赛时间轴生成器（F4 核心，技术路线 §7.1）——shared 纯函数，零运行时依赖。
 *
 * 语义：以赛事里程碑为锚点，按模板倒推出一组带截止日的备赛任务（日粒度）。
 * 纪律（§7.1 时区纪律）：
 * - 输入锚点时间一律按 UTC 解析并截断到日（YYYY-MM-DD），杜绝"差 8 小时算错一天"；
 * - confirmDate 是"队伍确认参赛日"（调用方传当天，日粒度），既是对齐会任务的截止日，
 *   也是过期判断与日期压缩的参照；
 * - 锚点缺失/已过期 → 整组跳过，不造垃圾任务；
 * - 倒推日期被收紧到 confirmDate 之后时标 compressed=true（前端显示"⚠ 时间紧张"）。
 */

export type PrepMilestoneKind = 'register_close' | 'prelim' | 'semi' | 'final' | 'custom';

/** 引擎输入的最小里程碑形态（后端由 event_milestones 行直接映射，不复制仓储类型）。 */
export interface PrepMilestone {
  kind: PrepMilestoneKind;
  label: string;
  /** ISO 8601（允许带时间，内部截断到日）。 */
  dueAt: string;
}

export interface PrepTask {
  title: string;
  /** YYYY-MM-DD（日粒度）。 */
  dueDate: string;
  anchor: PrepMilestoneKind;
  /** 倒推日期被收紧到 confirmDate 之后时为 true。 */
  compressed: boolean;
}

export interface PrepTemplateItem {
  /** 锚在哪个里程碑上。 */
  anchor: PrepMilestoneKind;
  /** 锚前 N 天（负值=锚后）。 */
  offsetDays: number;
  title: string;
}

/** 默认备赛模板：报名截止前组队/材料，初赛前路演准备（§7.1）。 */
export const DEFAULT_PREP_TEMPLATE: readonly PrepTemplateItem[] = [
  { anchor: 'register_close', offsetDays: -14, title: '完成队伍组建、绑定指导教师' },
  { anchor: 'register_close', offsetDays: -7, title: '完成 BP / 申报材料初稿' },
  { anchor: 'register_close', offsetDays: -2, title: '材料终审与提交' },
  { anchor: 'prelim', offsetDays: -14, title: '路演 PPT 初稿 + 彩排 1 次' },
];

const DAY_MS = 86_400_000;

/** 解析 ISO → UTC 日粒度毫秒（当天零点）；无法解析返回 null。 */
function dayMsOf(iso: string): number | null {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / DAY_MS) * DAY_MS;
}

/** UTC 日粒度毫秒 → YYYY-MM-DD。 */
function dayIsoOf(dayMs: number): string {
  return new Date(dayMs).toISOString().slice(0, 10);
}

/**
 * 生成备赛时间轴（纯函数，§7.1 伪代码的落地）。
 * @param milestones 赛事里程碑（锚点库）
 * @param confirmDate 队伍确认参赛日（YYYY-MM-DD 或 ISO，截断到日）
 * @param template 备赛模板（默认 DEFAULT_PREP_TEMPLATE，可整体覆盖）
 * @returns 按 dueDate 升序的备赛任务（首条为"召开组队对齐会"）
 */
export function buildPrepTimeline(
  milestones: readonly PrepMilestone[],
  confirmDate: string,
  template: readonly PrepTemplateItem[] = DEFAULT_PREP_TEMPLATE,
): PrepTask[] {
  const confirmMs = dayMsOf(confirmDate);
  if (confirmMs === null) return [];

  // 锚点索引：同 kind 取 dueAt 最早者
  const anchors = new Map<PrepMilestoneKind, number>();
  for (const milestone of milestones) {
    const dueMs = dayMsOf(milestone.dueAt);
    if (dueMs === null) continue;
    const existing = anchors.get(milestone.kind);
    if (existing === undefined || dueMs < existing) anchors.set(milestone.kind, dueMs);
  }

  const tasks: PrepTask[] = [];
  for (const item of template) {
    const anchorMs = anchors.get(item.anchor);
    if (anchorMs === undefined) continue; // 锚点缺失 → 整组跳过
    if (anchorMs < confirmMs) continue; // 已过期的锚点不生成
    let dueMs = anchorMs + item.offsetDays * DAY_MS;
    let compressed = false;
    if (dueMs <= confirmMs) {
      dueMs = confirmMs + DAY_MS; // 收紧到确认日之后（明天）
      compressed = true;
    }
    tasks.push({ title: item.title, dueDate: dayIsoOf(dueMs), anchor: item.anchor, compressed });
  }

  tasks.push({
    title: '召开组队对齐会、确定分工',
    dueDate: dayIsoOf(confirmMs),
    anchor: 'custom',
    compressed: false,
  });

  return tasks.sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0));
}
